import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import type { AmplifyOrgUser } from '@/types';
import { UserStatusBadge } from '@/components/tools/UserStatusBadge';

interface UserDetailDialogProps {
  user: AmplifyOrgUser | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function UserDetailDialog({ user, open, onOpenChange }: UserDetailDialogProps) {
  if (!user) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{user.name || 'Unknown User'}</DialogTitle>
          <DialogDescription className="font-mono text-xs">{user.uuid}</DialogDescription>
        </DialogHeader>

        <div className="space-y-5">
          {/* Basic Info */}
          <div className="grid grid-cols-2 gap-x-6 gap-y-4 text-sm">
            <div>
              <span className="text-muted-foreground">First Name</span>
              <p className="font-medium mt-0.5">{user.firstName ?? '—'}</p>
            </div>
            <div>
              <span className="text-muted-foreground">Last Name</span>
              <p className="font-medium mt-0.5">{user.lastName ?? '—'}</p>
            </div>
            {/* Email spans full width to prevent overflow */}
            <div className="col-span-2">
              <span className="text-muted-foreground">Email</span>
              <p className="font-medium mt-0.5 break-all">{user.email ?? '—'}</p>
            </div>
            <div>
              <span className="text-muted-foreground">Phone</span>
              <p className="font-medium mt-0.5">{user.phone ?? '—'}</p>
            </div>
            <div>
              {/* intentionally empty — keeps Phone in the left column */}
            </div>
            <div>
              <span className="text-muted-foreground">Status</span>
              <p className="mt-0.5"><UserStatusBadge status={user.status} /></p>
            </div>
            <div>
              <span className="text-muted-foreground">Role</span>
              <p className="mt-0.5">
                {user.organizationOwner ? (
                  <Badge variant="info">Owner</Badge>
                ) : user.universalAdmin ? (
                  <Badge variant="warning">Admin</Badge>
                ) : (
                  <Badge variant="secondary">User</Badge>
                )}
              </p>
            </div>
            <div>
              <span className="text-muted-foreground">Created</span>
              <p className="font-medium mt-0.5">{new Date(user.createdAt).toLocaleString()}</p>
            </div>
            <div>
              <span className="text-muted-foreground">Updated</span>
              <p className="font-medium mt-0.5">{new Date(user.updatedAt).toLocaleString()}</p>
            </div>
          </div>

          {/* Module Access */}
          {user.modules && user.modules.length > 0 && (
            <>
              <Separator />
              <div>
                <h4 className="text-sm font-semibold mb-3">Module Access ({user.modules.length})</h4>
                <div className="flex flex-wrap gap-2">
                  {user.modules.map((m) => (
                    <Badge key={m.id} variant="outline">
                      {m.name}
                    </Badge>
                  ))}
                </div>
              </div>
            </>
          )}

          {/* Status History */}
          {user.statusHistory && user.statusHistory.length > 0 && (
            <>
              <Separator />
              <div>
                <h4 className="text-sm font-semibold mb-3">Status History</h4>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Status</TableHead>
                      <TableHead>Changed At</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {user.statusHistory.map((sh, i) => (
                      <TableRow key={i}>
                        <TableCell>
                          <UserStatusBadge status={sh.status} />
                        </TableCell>
                        <TableCell className="text-muted-foreground">
                          {new Date(sh.updatedAt).toLocaleString()}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

